/**
 * Sun position — low-precision solar equations (NOAA/Meeus), accurate to
 * within a minute or so of sunrise/sunset, which is all a logbook's day/night
 * split needs. No network call: `ARCHITECTURE.md` §6 is explicit that this is
 * computed locally from the flight's own recorded position and time, not
 * fetched.
 */

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

function normaliseDeg(deg: number): number {
  const wrapped = deg % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

/** Days (with fraction) since the J2000.0 epoch, the clock every term below runs on. */
function julianCentury(instant: Date): number {
  const julianDay = instant.getTime() / 86_400_000 + 2_440_587.5;
  return (julianDay - 2_451_545) / 36_525;
}

interface SunPosition {
  declinationDeg: number;
  /** Minutes the sundial runs ahead of (+) or behind (-) a clock, from the earth's tilt and orbital eccentricity. */
  equationOfTimeMin: number;
}

function sunPosition(instant: Date): SunPosition {
  const t = julianCentury(instant);

  const meanLongitudeDeg = normaliseDeg(280.466_46 + t * (36_000.769_83 + t * 0.000_303_2));
  const meanAnomalyDeg = normaliseDeg(357.529_11 + t * (35_999.050_29 - 0.000_153_7 * t));
  const eccentricity = 0.016_708_634 - t * (0.000_042_037 + 0.000_000_126_7 * t);

  const meanAnomalyRad = meanAnomalyDeg * DEG_TO_RAD;
  const centerDeg =
    Math.sin(meanAnomalyRad) * (1.914_602 - t * (0.004_817 + 0.000_014 * t)) +
    Math.sin(2 * meanAnomalyRad) * (0.019_993 - 0.000_101 * t) +
    Math.sin(3 * meanAnomalyRad) * 0.000_289;

  const trueLongitudeDeg = meanLongitudeDeg + centerDeg;
  const omegaDeg = 125.04 - 1_934.136 * t;
  const apparentLongitudeDeg =
    trueLongitudeDeg - 0.005_69 - 0.004_78 * Math.sin(omegaDeg * DEG_TO_RAD);

  const meanObliquityDeg =
    23 + (26 + (21.448 - t * (46.815 + t * (0.000_59 - t * 0.001_813))) / 60) / 60;
  const obliquityCorrDeg = meanObliquityDeg + 0.002_56 * Math.cos(omegaDeg * DEG_TO_RAD);

  const declinationDeg =
    Math.asin(
      Math.sin(obliquityCorrDeg * DEG_TO_RAD) * Math.sin(apparentLongitudeDeg * DEG_TO_RAD),
    ) * RAD_TO_DEG;

  const y = Math.tan((obliquityCorrDeg / 2) * DEG_TO_RAD) ** 2;
  const meanLongitudeRad = meanLongitudeDeg * DEG_TO_RAD;
  const equationOfTimeMin =
    4 *
    RAD_TO_DEG *
    (y * Math.sin(2 * meanLongitudeRad) -
      2 * eccentricity * Math.sin(meanAnomalyRad) +
      4 * eccentricity * y * Math.sin(meanAnomalyRad) * Math.cos(2 * meanLongitudeRad) -
      0.5 * y * y * Math.sin(4 * meanLongitudeRad) -
      1.25 * eccentricity * eccentricity * Math.sin(2 * meanAnomalyRad));

  return { declinationDeg, equationOfTimeMin };
}

/**
 * The sun's angle above the horizon at a position and instant, in degrees.
 * Negative means below the horizon — civil twilight is -6°, the threshold
 * `isNight` uses.
 */
export function solarElevationDeg(instant: Date, latDeg: number, lonDeg: number): number {
  const { declinationDeg, equationOfTimeMin } = sunPosition(instant);

  const utcMinutes =
    instant.getUTCHours() * 60 + instant.getUTCMinutes() + instant.getUTCSeconds() / 60;
  let trueSolarTimeMin = (utcMinutes + equationOfTimeMin + 4 * lonDeg) % 1440;
  if (trueSolarTimeMin < 0) {
    trueSolarTimeMin += 1440;
  }

  let hourAngleDeg = trueSolarTimeMin / 4 - 180;
  if (hourAngleDeg < -180) {
    hourAngleDeg += 360;
  }

  const latRad = latDeg * DEG_TO_RAD;
  const declRad = declinationDeg * DEG_TO_RAD;
  const hourAngleRad = hourAngleDeg * DEG_TO_RAD;

  const elevationRad = Math.asin(
    Math.sin(latRad) * Math.sin(declRad) +
      Math.cos(latRad) * Math.cos(declRad) * Math.cos(hourAngleRad),
  );
  return elevationRad * RAD_TO_DEG;
}

/** Sun elevation at or below this is civil twilight or darker — the NZ CAA's working definition of "night". */
export const CIVIL_TWILIGHT_ELEVATION_DEG = -6;

export function isNight(instant: Date, latDeg: number, lonDeg: number): boolean {
  return solarElevationDeg(instant, latDeg, lonDeg) < CIVIL_TWILIGHT_ELEVATION_DEG;
}
